import React, { useState, useCallback } from 'react';
import useIntlExtend from '@intelligence/hooks/useIntlExtend';
import Bubble from '@intelligence/v3/components/Bubble';
import WeixinIcon from '@intelligence/assets/imgs/casualDailyReports/icon-weixin.png';
import DouyinIcon from '@intelligence/assets/imgs/casualDailyReports/icon-dy.png';
import ProductListModal from '@intelligence/components/ProductListModal';
import type { IProductItem } from '@intelligence/components/ProductListModal/types';
import './index.scss';

export interface PublisherWithPopoverProps {
  /** 微信平台发行商（支持国际化） */
  wechatPublisher?: string;
  /** 抖音平台发行商（支持国际化） */
  douyinPublisher?: string;
  /** 兜底发行商（当未区分微信/抖音平台时使用） */
  publisher?: string;
  /** 同公司产品列表，有数据时支持悬浮提示和点击打开弹窗 */
  products?: IProductItem[];
}

/** 发行商展示组件，分微信与抖音平台展示发行商信息，存在同公司产品时点击打开产品列表弹窗 */
const PublisherWithPopover: React.FC<PublisherWithPopoverProps> = ({
  wechatPublisher,
  douyinPublisher,
  publisher,
  products = [],
}) => {
  const { formatMessage } = useIntlExtend();
  const [modalVisible, setModalVisible] = useState(false);

  const handleOpen = useCallback(() => setModalVisible(true), []);
  const handleClose = useCallback(() => setModalVisible(false), []);

  /** 微信平台发行商行是否渲染 */
  const hasWechat = Boolean(wechatPublisher);
  /** 抖音平台发行商行是否渲染 */
  const hasDouyin = Boolean(douyinPublisher);
  /** 微信与抖音发行商是否相同（相同则合并为双平台图标+单行发行商） */
  const isSamePublisher = Boolean(
    hasWechat && hasDouyin && wechatPublisher === douyinPublisher,
  );
  /** 兜底单行发行商是否渲染（在微信和抖音发行商均为空时生效） */
  const hasFallback = !hasWechat && !hasDouyin && Boolean(publisher);
  /** 是否存在同公司旗下产品列表，用于决定是否启用 Hover 提示与弹窗交互 */
  const hasProducts = Boolean(products && products.length > 0);

  if (!hasWechat && !hasDouyin && !hasFallback) {
    return null;
  }

  const renderItems = () => {
    // 微信与抖音发行商相同时，合并为单行展示：[微信icon] [抖音icon] 发行商名称
    if (isSamePublisher) {
      return (
        <div className="dbi-publisher-with-popover__item" key="combined">
          <img
            src={WeixinIcon}
            alt="WeChat"
            className="dbi-publisher-with-popover__platform-icon"
          />
          <img
            src={DouyinIcon}
            alt="Douyin"
            className="dbi-publisher-with-popover__platform-icon"
          />
          <span className="dbi-publisher-with-popover__name">{wechatPublisher}</span>
        </div>
      );
    }

    return (
      <>
        {hasWechat && (
          <div className="dbi-publisher-with-popover__item" key="wechat">
            <img
              src={WeixinIcon}
              alt="WeChat"
              className="dbi-publisher-with-popover__platform-icon"
            />
            <span className="dbi-publisher-with-popover__name">{wechatPublisher}</span>
          </div>
        )}
        {hasDouyin && (
          <div className="dbi-publisher-with-popover__item" key="douyin">
            <img
              src={DouyinIcon}
              alt="Douyin"
              className="dbi-publisher-with-popover__platform-icon"
            />
            <span className="dbi-publisher-with-popover__name">{douyinPublisher}</span>
          </div>
        )}
        {hasFallback && (
          <div className="dbi-publisher-with-popover__item" key="fallback">
            <span className="dbi-publisher-with-popover__name">{publisher}</span>
          </div>
        )}
      </>
    );
  };

  if (!hasProducts) {
    return (
      <div className="dbi-publisher-with-popover">
        <div className="dbi-publisher-with-popover__static">
          {renderItems()}
        </div>
      </div>
    );
  }

  /** 弹窗头部副标题所展示的公司名称，优先使用通用发行商或一致的平台发行商 */
  const modalPublisherTitle =
    publisher ||
    (wechatPublisher && douyinPublisher && wechatPublisher === douyinPublisher
      ? wechatPublisher
      : (wechatPublisher || douyinPublisher || ''));

  return (
    <>
      <div className="dbi-publisher-with-popover">
        <Bubble
          content={formatMessage({ id: 'dbi_publisher_hover_tip' })}
          placement="bottom"
        >
          <button
            type="button"
            className="dbi-publisher-with-popover__trigger"
            onClick={handleOpen}
          >
            {renderItems()}
          </button>
        </Bubble>
      </div>

      <ProductListModal
        visible={modalVisible}
        onClose={handleClose}
        publisher={modalPublisherTitle}
        products={products}
      />
    </>
  );
};

export default PublisherWithPopover;
